import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { useNavigate, Link, useLocation } from 'react-router-dom';

import axios from 'axios';

import {

  Table,

  Button,

  Modal,

  Form,

  Input,

  Select,

  DatePicker,

  Typography,

  Space,

  notification,

  Popconfirm,

  Tag,

  Card,

  Row,

  Col,

  Divider

} from 'antd';

import {

  PlusOutlined,

  EditOutlined,

  DeleteOutlined,

  DashboardOutlined,

  UserOutlined,

  SettingOutlined,

  DesktopOutlined,

  MenuOutlined,

  CloseOutlined,

  SyncOutlined,

  FileTextOutlined,

  SafetyOutlined,

  GlobalOutlined,

  CheckCircleOutlined,

  RocketOutlined

} from '@ant-design/icons';

import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';

import timezone from 'dayjs/plugin/timezone';

import ResourceManagement from './ResourceManagement';

import { formatDateInTimezone, getBrowserTimezone, getTimezoneOptions } from '../lib/timezones';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';



dayjs.extend(utc);

dayjs.extend(timezone);



const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const { Text } = Typography;



// Admin sidebar menu

const adminMenu = [

  { key: 'overview', icon: <DashboardOutlined />, label: 'Overview' },

  { key: 'resources', icon: <DesktopOutlined />, label: 'Resources' },

  { key: 'users', icon: <UserOutlined />, label: 'Users' },

  { key: 'orders', icon: <FileTextOutlined />, label: 'Orders' },

  { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },

];



// Notification items







function Admin() {

  const navigate = useNavigate();

  const location = useLocation();
  const { formatFromEur, formatAmount } = useCurrency();

  const [activeSidebarKey, setActiveSidebarKey] = useState('overview');

  const [resources, setResources] = useState([]);

  const [users, setUsers] = useState([]);

  const [orders, setOrders] = useState([]);

  const [showUserModal, setShowUserModal] = useState(false);

  const [userForm] = Form.useForm();

  const [editingUser, setEditingUser] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [timezoneOptions] = useState(() => getTimezoneOptions());

  const [systemTimezone, setSystemTimezone] = useState('UTC');

  const [adminPanelTimezone, setAdminPanelTimezone] = useState(getBrowserTimezone());

  const [timezoneLoading, setTimezoneLoading] = useState(false);

  

  // Order management states

  const [showOrderModal, setShowOrderModal] = useState(false);

  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);

  const [editingOrder, setEditingOrder] = useState(null);

  const [orderForm] = Form.useForm();

  const [createOrderForm] = Form.useForm();

  

  // Components selection states

  const [resourceComponents, setResourceComponents] = useState([]);

  const [selectedComponents, setSelectedComponents] = useState([]);

  const [currentResource, setCurrentResource] = useState(null);

  const [editResourceComponents, setEditResourceComponents] = useState([]);

  const [editSelectedComponentIds, setEditSelectedComponentIds] = useState([]);

  const [ordersPage, setOrdersPage] = useState(1);

  const [usersPage, setUsersPage] = useState(1);

  const ordersPageSize = 6;

  const usersPageSize = 10;

  useEffect(() => {
    setPageMeta({
      title: 'Admin Panel | Clouniq',
      description: 'Manage users, resources, orders, and platform settings.',
    });
  }, []);



  useEffect(() => {

    if (location.pathname === '/admin/resources') {

      setActiveSidebarKey('resources');

    }

  }, [location.pathname]);



  useEffect(() => {

    setMobileSidebarOpen(false);

  }, [location.pathname, activeSidebarKey]);



  useEffect(() => {

    setOrdersPage(1);

  }, [orders.length]);



  useEffect(() => {

    setUsersPage(1);

  }, [users.length]);



  useEffect(() => {

    const handleResize = () => {

      if (window.innerWidth > 992) {

        setSidebarCollapsed(false);

      }

    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);

  }, []);



  // Group components by type

  const groupedComponents = useMemo(() => resourceComponents.reduce((acc, comp) => {

    if (!acc[comp.type]) acc[comp.type] = [];

    acc[comp.type].push(comp);

    return acc;

  }, {}), [resourceComponents]);



  const groupedEditComponents = useMemo(() => editResourceComponents.reduce((acc, comp) => {

    if (!acc[comp.type]) acc[comp.type] = [];

    acc[comp.type].push(comp);

    return acc;

  }, {}), [editResourceComponents]);



  // Calculate components cost

  const componentsCost = useMemo(() => selectedComponents.reduce((sum, comp) => {

    return sum + (comp.is_free ? 0 : parseFloat(comp.price_delta || 0));

  }, 0), [selectedComponents]);



  // Calculate total order cost

  const basePrice = useMemo(() => parseFloat(currentResource?.base_price_per_hour || currentResource?.price_per_hour || 0), [currentResource]);

  const totalOrderCost = basePrice + componentsCost;



  const editStartTime = Form.useWatch('start_time', orderForm);

  const editEndTime = Form.useWatch('end_time', orderForm);

  const editResourceId = Form.useWatch('resource_id', orderForm);

  const editItems = Form.useWatch('items', orderForm);



  const computeHoursBetween = useCallback((startValue, endValue) => {

    if (!startValue || !endValue) return null;

    const start = dayjs.isDayjs(startValue) ? startValue.toDate() : new Date(startValue);

    const end = dayjs.isDayjs(endValue) ? endValue.toDate() : new Date(endValue);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

    const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

    return diff > 0 ? diff : null;

  }, []);



  const editHours = useMemo(() => {

    const hours = computeHoursBetween(editStartTime, editEndTime);

    return Number.isFinite(hours) ? hours : 0;

  }, [editStartTime, editEndTime, computeHoursBetween]);



  const estimatedOrderCost = useMemo(() => {

    const selectedResource = resources.find((r) => Number(r.id) === Number(editResourceId));

    const selectedBasePrice = parseFloat(selectedResource?.base_price_per_hour || selectedResource?.price_per_hour || 0);

    const itemsCostPerHour = editSelectedComponentIds

      .map((id) => editResourceComponents.find((comp) => Number(comp.id) === Number(id)))

      .filter(Boolean)

      .reduce((sum, comp) => sum + (comp.is_free ? 0 : Number(comp.price_delta || 0)), 0);

    return (selectedBasePrice + itemsCostPerHour) * editHours;

  }, [resources, editResourceId, editSelectedComponentIds, editResourceComponents, editHours]);



  const estimatedHourlyRate = useMemo(() => {

    if (editHours <= 0) return 0;

    return estimatedOrderCost / editHours;

  }, [estimatedOrderCost, editHours]);



  const pagedOrders = useMemo(() => orders.slice(

    (ordersPage - 1) * ordersPageSize,

    ordersPage * ordersPageSize,

  ), [orders, ordersPage, ordersPageSize]);



  const pagedUsers = useMemo(() => users.slice(

    (usersPage - 1) * usersPageSize,

    usersPage * usersPageSize,

  ), [users, usersPage, usersPageSize]);



  const createStartTime = Form.useWatch('start_time', createOrderForm);

  const createEndTime = Form.useWatch('end_time', createOrderForm);



  useEffect(() => {

    const autoHours = computeHoursBetween(createStartTime, createEndTime);

    if (autoHours) {

      const wholeHours = Math.floor(autoHours);

      const minutes = Math.round((autoHours - wholeHours) * 60);

      const normalizedMinutes = minutes === 60 ? 0 : minutes;

      const normalizedHours = minutes === 60 ? wholeHours + 1 : wholeHours;

      createOrderForm.setFieldValue('hours', normalizedHours);

      createOrderForm.setFieldValue('minutes', normalizedMinutes);

    }

  }, [createStartTime, createEndTime, createOrderForm, computeHoursBetween]);



  useEffect(() => {

    if (!showOrderModal) return;

    if (!Number.isFinite(estimatedOrderCost)) return;

    orderForm.setFieldValue('total_cost', estimatedOrderCost.toFixed(2));

  }, [editItems, editResourceId, editHours, showOrderModal, estimatedOrderCost, orderForm]);



  // Fetch resource components

  const handleResourceChange = async (resourceId) => {

    const resource = resources.find(r => r.id === resourceId);

    setCurrentResource(resource);

    

    if (resourceId) {

      try {

        const token = localStorage.getItem('token');

        const res = await axios.get(`${API_URL}/api/orders/resources/${resourceId}/components`, {

          headers: { Authorization: `Bearer ${token}` }

        });

        setResourceComponents(res.data || []);

        setSelectedComponents([]);

      } catch (err) {

        console.error('Failed to fetch components:', err);

        setResourceComponents([]);

      }

    } else {

      setResourceComponents([]);

      setSelectedComponents([]);

    }

  };



  // Handle component selection

  const handleComponentSelect = (type, ids) => {

    const typeComponents = resourceComponents.filter(c => c.type === type);

    const normalizedIds = Array.isArray(ids) ? ids : (ids ? [ids] : []);

    const selected = typeComponents.filter(c => normalizedIds.includes(c.id));

    

    // Remove all components of this type from selected

    const filtered = selectedComponents.filter(c => c.type !== type);

    setSelectedComponents([...filtered, ...selected]);

  };



  const handleEditResourceChange = async (resourceId) => {

    const currentItems = Array.isArray(orderForm.getFieldValue('items'))

      ? orderForm.getFieldValue('items')

      : [];

    await loadEditResourceComponents(resourceId, currentItems);

  };



  const handleEditComponentSelect = (type, ids) => {

    const normalizedTypeIds = editResourceComponents

      .filter((comp) => comp.type === type)

      .map((comp) => Number(comp.id));

    const nextTypeIds = (Array.isArray(ids) ? ids : (ids ? [ids] : [])).map((id) => Number(id));

    const nextSelectedIds = [

      ...editSelectedComponentIds.filter((id) => !normalizedTypeIds.includes(Number(id))),

      ...nextTypeIds,

    ];

    setEditSelectedComponentIds(nextSelectedIds);

    syncEditItemsFromSelection(nextSelectedIds, editResourceComponents);

  };



  // Modified handleCreateOrder to include components

  const handleCreateOrderWithComponents = async (values) => {

    const token = localStorage.getItem('token');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    const autoHours = computeHoursBetween(values.start_time, values.end_time);

    const manualHours = (Number(values.hours) || 0) + (Number(values.minutes) || 0) / 60;

    const finalHours = Number.isFinite(autoHours) && autoHours > 0 ? autoHours : manualHours;

    try {

      const resolvedSystemTz = systemTimezone || 'UTC';

      const createdValue = values.created_at ?? null;

      const startValue = values.start_time ?? null;

      const endValue = values.end_time ?? null;

      const createdIso = createdValue

        ? dayjs.tz(dayjs(createdValue).format('YYYY-MM-DD HH:mm'), resolvedSystemTz).toISOString()

        : null;

      const startIso = startValue

        ? dayjs.tz(dayjs(startValue).format('YYYY-MM-DD HH:mm'), resolvedSystemTz).toISOString()

        : null;

      const endIso = endValue

        ? dayjs.tz(dayjs(endValue).format('YYYY-MM-DD HH:mm'), resolvedSystemTz).toISOString()

        : null;

      await axios.post(`${API_URL}/api/orders/admin`, {

        user_id: values.user_id,

        resource_id: values.resource_id,

        hours: finalHours,

        created_at: createdIso,

        start_time: startIso,

        end_time: endIso,

        items: selectedComponents.map(c => ({

          name: c.name,

          value: c.value,

          price_delta: c.price_delta,

          is_free: c.is_free

        })), 

        payment_id: values.payment_id,

      }, config);

      notification.success({ message: 'Order created successfully' });

      setShowCreateOrderModal(false);

      createOrderForm.resetFields();

      setSelectedComponents([]);

      setResourceComponents([]);

      setCurrentResource(null);

      fetchData();

    } catch (err) {

      notification.error({ message: 'Failed to create order' });

    }

  };



  const loadTimezoneSettings = useCallback(async (config) => {

    try {

      const res = await axios.get(`${API_URL}/api/settings/timezones`, config);

      const data = res?.data || {};

      if (data.systemTimezone) setSystemTimezone(data.systemTimezone);

      if (data.adminPanelTimezone) setAdminPanelTimezone(data.adminPanelTimezone);

    } catch (err) {

      console.error('Failed to load timezone settings:', err);

    }

  }, []);



  const saveSystemTimezone = async () => {

    const token = localStorage.getItem('token');

    if (!token) return;

    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {

      setTimezoneLoading(true);

      await axios.put(`${API_URL}/api/settings/system-timezone`, { timezone: systemTimezone }, config);

      notification.success({ message: 'System timezone updated' });

      await loadTimezoneSettings(config);

    } catch (err) {

      notification.error({ message: 'Failed to update system timezone' });

    } finally {

      setTimezoneLoading(false);

    }

  };



  const saveAdminPanelTimezone = async () => {

    const token = localStorage.getItem('token');

    if (!token) return;

    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {

      setTimezoneLoading(true);

      await axios.put(`${API_URL}/api/settings/admin-timezone`, { timezone: adminPanelTimezone }, config);

      notification.success({ message: 'Admin timezone updated' });

      await loadTimezoneSettings(config);

    } catch (err) {

      notification.error({ message: 'Failed to update admin timezone' });

    } finally {

      setTimezoneLoading(false);

    }

  };



  const fetchData = useCallback(async () => {

    const token = localStorage.getItem('token');

    if (!token) {

      navigate('/login');

      return;

    }

    const config = { headers: { Authorization: `Bearer ${token}` } };

    

    try {

      setLoading(true);

      const [resRes, usersRes] = await Promise.all([

        axios.get(`${API_URL}/api/resources?lite=1&available=0`),

        axios.get(`${API_URL}/api/users`, config),

      ]);

      const resourcesData = Array.isArray(resRes.data?.data)

        ? resRes.data.data

        : (Array.isArray(resRes.data) ? resRes.data : []);

      setResources(resourcesData);

      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);



      const orderLimit = 100;

      const ordersFirstRes = await axios.get(`${API_URL}/api/orders/all`, {

        ...config,

        params: { limit: orderLimit, offset: 0 },

      });

      const firstBatch = Array.isArray(ordersFirstRes.data?.data) ? ordersFirstRes.data.data : [];

      const totalOrders = Number(ordersFirstRes.data?.total || firstBatch.length);

      const dedupeOrders = (items) => {

        const map = new Map();

        (items || []).forEach((order) => {

          const key = order?.order_number ?? order?.id;

          if (key === undefined) return;

          if (!map.has(key)) map.set(key, order);

        });

        return Array.from(map.values());

      };

      if (firstBatch.length < totalOrders) {

        const allOrders = [...firstBatch];

        let offset = firstBatch.length;

        while (offset < totalOrders) {

          const nextRes = await axios.get(`${API_URL}/api/orders/all`, {

            ...config,

            params: { limit: orderLimit, offset },

          });

          const nextBatch = Array.isArray(nextRes.data?.data) ? nextRes.data.data : [];

          if (nextBatch.length === 0) break;

          allOrders.push(...nextBatch);

          offset += nextBatch.length;

        }

        const sorted = dedupeOrders(allOrders).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setOrders(sorted);

      } else {

        const sorted = dedupeOrders(firstBatch).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setOrders(sorted);

      }

      await loadTimezoneSettings(config);

    } catch (err) {

      console.error('Failed to fetch data:', err);

      // Only logout on authentication errors, not on network/server errors

      if (err.response?.status === 401) {

        // Token expired or invalid

        localStorage.removeItem('token');

        navigate('/login');

      } else if (err.response?.status === 403) {

        // User not authorized for this action

        notification.error({ 

          message: 'Access Denied', 

          description: 'You do not have permission to perform this action.' 

        });

      } else {

        // Network or server error - don't logout, just show error

        notification.error({ 

          message: 'Connection Error', 

          description: 'Unable to load data. Please try again.' 

        });

      }

    } finally {

      setLoading(false);

    }

  }, [loadTimezoneSettings, navigate]);



  useEffect(() => {

    fetchData();

  }, [fetchData]);



  // handleSubmit removed as resource management moved to dedicated page



  const handleUserSubmit = async (values) => {

    const token = localStorage.getItem('token');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    

    try {

      if (editingUser) {

        const payload = {

          firstName: values.firstName || '',

          lastName: values.lastName || '',

          email: values.email,

          role: values.role,

          timezone: values.timezone,

        };

        if (values.password && values.password.trim()) {

          payload.password = values.password.trim();

        }



        await axios.put(`${API_URL}/api/users/${editingUser.id}`, payload, config);

        notification.success({ message: 'User updated successfully' });

      }

      fetchData();

      setShowUserModal(false);

      userForm.resetFields();

      setEditingUser(null);

    } catch (err) {

      notification.error({ message: 'Failed to update user' });

    }

  };



  // handleEdit removed as resource management moved to dedicated page



  const handleEditUser = (user) => {

    userForm.setFieldsValue({

      firstName: user.firstName || '',

      lastName: user.lastName || '',

      email: user.email || '',

      role: user.role || 'user',

      timezone: user.timezone || 'UTC',

      password: '',

    });

    setEditingUser(user);

    setShowUserModal(true);

  };



  // handleDelete removed as resource management moved to dedicated page



  const handleDeleteUser = async (id) => {

    const token = localStorage.getItem('token');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {

      await axios.delete(`${API_URL}/api/users/${id}`, config);

      notification.success({ message: 'User deleted successfully' });

      fetchData();

    } catch (err) {

      notification.error({ message: 'Failed to delete user' });

    }

  };



  // Order management functions

  const openOrderEditor = (order) => {

    const mappedItems = Array.isArray(order.items)

      ? order.items.map((item) => ({

        name: item.name || '',

        value: item.value || '',

        price_delta: Number(item.price_delta || 0),

        is_free: !!item.is_free,

      }))

      : [];



    const resolvedSystemTz = systemTimezone || 'UTC';

    orderForm.setFieldsValue({

      user_id: order.user_id,

      resource_id: order.resource_id,

      status: order.status,

      created_at: order.createdAt ? dayjs(order.createdAt).tz(resolvedSystemTz) : null,

      start_time: order.start_time ? dayjs(order.start_time).tz(resolvedSystemTz) : null,

      end_time: order.end_time ? dayjs(order.end_time).tz(resolvedSystemTz) : null,

      total_cost: Number(order.total_cost || 0),

      payment_id: order.payment_id || '',

      items: mappedItems,

    });

    setEditingOrder(order);

    setShowOrderModal(true);

    loadEditResourceComponents(order.resource_id, mappedItems);

  };

  const handleEditOrder = (order) => openOrderEditor(order);



  const handleUpdateOrder = async (values) => {

    const token = localStorage.getItem('token');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {

      const itemsFromForm = orderForm.getFieldValue('items');

      const normalizedItems = Array.isArray(itemsFromForm)

        ? itemsFromForm

          .filter((item) => item?.name && item?.value)

          .map((item) => ({

            name: item.name,

            value: item.value,

            price_delta: Number(item.price_delta || 0),

            is_free: !!item.is_free,

          }))

        : [];

      const resolvedSystemTz = systemTimezone || 'UTC';

      const createdValue = values.created_at ?? (editingOrder?.createdAt ? dayjs(editingOrder.createdAt).tz(resolvedSystemTz) : null);

      const startValue = values.start_time ?? (editingOrder?.start_time ? dayjs(editingOrder.start_time).tz(resolvedSystemTz) : null);

      const endValue = values.end_time ?? (editingOrder?.end_time ? dayjs(editingOrder.end_time).tz(resolvedSystemTz) : null);

      const payload = {

        user_id: values.user_id,

        resource_id: values.resource_id,

        status: values.status,

        payment_id: values.payment_id || null,

        items: normalizedItems,

      };

      if (createdValue) {

        payload.created_at = dayjs.tz(dayjs(createdValue).format('YYYY-MM-DD HH:mm'), resolvedSystemTz).toISOString();

      }

      if (startValue) {

        payload.start_time = dayjs.tz(dayjs(startValue).format('YYYY-MM-DD HH:mm'), resolvedSystemTz).toISOString();

      }

      if (endValue) {

        payload.end_time = dayjs.tz(dayjs(endValue).format('YYYY-MM-DD HH:mm'), resolvedSystemTz).toISOString();

      }

      await axios.put(`${API_URL}/api/orders/${editingOrder.order_number}`, payload, config);

      notification.success({ message: 'Order updated successfully' });

      setShowOrderModal(false);

      setEditingOrder(null);

      setEditResourceComponents([]);

      setEditSelectedComponentIds([]);

      orderForm.resetFields();

      fetchData();

    } catch (err) {

      console.error('Update order failed:', err);

      notification.error({

        message: 'Failed to update order',

        description: err.response?.data?.message || err.message,

      });

    }

  };



  const handleDeleteOrder = async (order) => {

    const token = localStorage.getItem('token');

    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {

      await axios.delete(`${API_URL}/api/orders/${order.order_number}`, config);

      notification.success({ message: 'Order deleted successfully' });

      fetchData();

    } catch (err) {

      notification.error({ message: 'Failed to delete order' });

    }

  };



  const handleSidebarChange = (key) => {

    setActiveSidebarKey(key);

    setMobileSidebarOpen(false);

    if (key === 'resources' && location.pathname !== '/admin/resources') {

      navigate('/admin/resources', { replace: true });

      return;

    }

    if (key !== 'resources' && location.pathname !== '/admin') {

      navigate('/admin', { replace: true });

    }

  };



  const fetchComponentsByResourceId = useCallback(async (resourceId) => {

    if (!resourceId) return [];

    const token = localStorage.getItem('token');

    const res = await axios.get(`${API_URL}/api/orders/resources/${resourceId}/components`, {

      headers: { Authorization: `Bearer ${token}` },

    });

    return Array.isArray(res.data) ? res.data : [];

  }, []);



  const syncEditItemsFromSelection = useCallback((selectedIds, components) => {

    const selectedIdSet = new Set((selectedIds || []).map((id) => Number(id)));

    const mappedItems = (components || [])

      .filter((comp) => selectedIdSet.has(Number(comp.id)))

      .map((comp) => ({

        name: comp.name,

        value: comp.value,

        price_delta: Number(comp.price_delta || 0),

        is_free: !!comp.is_free,

      }));

    orderForm.setFieldValue('items', mappedItems);

  }, [orderForm]);



  const loadEditResourceComponents = useCallback(async (resourceId, existingItems = []) => {

    try {

      const components = await fetchComponentsByResourceId(resourceId);

      setEditResourceComponents(components);



      const selectedIds = components

        .filter((comp) => existingItems.some((item) => item?.name === comp.name && item?.value === comp.value))

        .map((comp) => Number(comp.id));



      setEditSelectedComponentIds(selectedIds);

      syncEditItemsFromSelection(selectedIds, components);

    } catch (err) {

      console.error('Failed to fetch edit components:', err);

      setEditResourceComponents([]);

      setEditSelectedComponentIds([]);

      orderForm.setFieldValue('items', []);

    }

  }, [fetchComponentsByResourceId, orderForm, syncEditItemsFromSelection]);



  const openAddModal = () => {

    handleSidebarChange('resources');

  };



  const handleSidebarToggle = () => {

    if (window.innerWidth <= 992) {

      setMobileSidebarOpen(false);

      return;

    }

    setSidebarCollapsed(false);

  };





  const userColumns = [

    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },

    { title: 'Name', key: 'name', render: (_, record) => `${record.firstName || ''} ${record.lastName || ''}` },

    { title: 'Email', dataIndex: 'email', key: 'email' },

    { title: 'Role', dataIndex: 'role', key: 'role', render: (role) => <Tag color={role === 'admin' ? 'red' : 'blue'}>{role}</Tag> },

    { title: 'Joined', dataIndex: 'createdAt', key: 'createdAt', render: (date) => formatDateInTimezone(date, adminPanelTimezone) },

    {

      title: 'Actions',

      key: 'actions',

      render: (_, record) => (

        <Space size="small">

          <Button icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); handleEditUser(record); }} className="admin-action-btn" />

          <Popconfirm title="Delete this user?" onConfirm={() => handleDeleteUser(record.id)} okText="Yes" cancelText="No">

            <Button icon={<DeleteOutlined />} danger className="admin-action-btn" onClick={(e) => e.stopPropagation()} />

          </Popconfirm>

        </Space>

      ),

    },

  ];



  const orderColumns = [

    { title: 'Order #', dataIndex: 'order_number', key: 'order_number' },

    { title: 'User', dataIndex: ['User', 'email'], key: 'user' },

    { title: 'Resource', dataIndex: ['Resource', 'name'], key: 'resource' },

    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'active' ? 'green' : s === 'completed' ? 'blue' : 'orange'}>{s}</Tag> },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (s) => {
        const normalized = s === 'pending' ? 'payment_awaited' : s === 'failed' ? 'payment_failed' : s;
        return (
          <Tag color={normalized === 'paid' ? 'green' : normalized === 'payment_failed' ? 'red' : 'orange'}>
            {normalized === 'paid' ? 'Paid' : normalized === 'payment_failed' ? 'Payment failed' : 'Payment awaited'}
          </Tag>
        );
      },
    },

    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', render: (d) => formatDateInTimezone(d, adminPanelTimezone) },
    { title: 'Start', dataIndex: 'start_time', key: 'start', render: (d) => formatDateInTimezone(d, adminPanelTimezone) },

    { title: 'End', dataIndex: 'end_time', key: 'end', render: (d) => formatDateInTimezone(d, adminPanelTimezone) },

    {
      title: 'Cost',
      dataIndex: 'total_cost',
      key: 'cost',
      render: (c, record) => {
        const currency = record?.currency || record?.order_currency;
        return currency ? formatAmount(Number(c || 0), currency) : formatFromEur(Number(c || 0));
      },
    },
    { title: 'Paynet Order ID', key: 'paynet', render: (_, record) => record?.payment?.payNetOrderId || '-' },

    {

      title: 'Actions',

      key: 'actions',

      render: (_, record) => (

        <Space size="small">

          <Button

            size="small"

            className="admin-action-btn admin-order-open-btn"

            icon={<FileTextOutlined />}

            onClick={(event) => {

              event.stopPropagation();

              navigate(`/admin/order/${record.order_number}`);

            }}

          />

          <Button

            icon={<EditOutlined />}

            onClick={(event) => {

              event.stopPropagation();

              handleEditOrder(record);

            }}

            className="admin-action-btn"

          />

          <Popconfirm

            title="Delete this order?"

            onConfirm={() => handleDeleteOrder(record)}

            okText="Yes"

            cancelText="No"

          >

            <Button

              icon={<DeleteOutlined />}

              danger

              className="admin-action-btn"

              onClick={(event) => event.stopPropagation()}

            />

          </Popconfirm>

        </Space>

      ),

    },

  ];




  return (

    <div className={`dashboard-layout workspace-page admin-panel ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>

      <div className="workspace-backdrop"></div>

      {mobileSidebarOpen && (

        <button

          className="sidebar-overlay"

          aria-label="Close menu"

          onClick={() => setMobileSidebarOpen(false)}

        />

      )}



      {/* Sidebar */}

      <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'open' : ''}`}>

        <div className="sidebar-header">

          <Link to="/" className="sidebar-brand">

            <RocketOutlined />

            {!sidebarCollapsed && <span>CLOUNIQ</span>}

          </Link>

          <button className="sidebar-toggle" onClick={handleSidebarToggle}>

            {sidebarCollapsed ? <MenuOutlined /> : <CloseOutlined />}

          </button>

        </div>

        

        <nav className="sidebar-nav">

          {adminMenu.map(item => (

            <button

              key={item.key}

              className={`sidebar-menu-item ${activeSidebarKey === item.key ? 'active' : ''}`}

              onClick={() => handleSidebarChange(item.key)}

            >

              {item.icon}

              {!sidebarCollapsed && <span>{item.label}</span>}

            </button>

          ))}

        </nav>



        <div className="sidebar-footer">

          <div className="sidebar-user">

            <div className="user-avatar">

              <SafetyOutlined />

            </div>

            {!sidebarCollapsed && (

              <div className="user-info">

                <Text className="user-name">Admin</Text>

                <Tag color="red" className="user-role">Administrator</Tag>

              </div>

            )}

          </div>

        </div>

      </aside>



      {/* Main Content */}

      <main className="dashboard-main">

        {/* Header */}

        <header className="dashboard-header workspace-shell">

          <div className="header-left">

            <button

              className="mobile-menu-btn"

              aria-label="Open navigation"

              onClick={() => setMobileSidebarOpen(true)}

            >

              <MenuOutlined />

            </button>

            <h1 className="page-title">

              {adminMenu.find(m => m.key === activeSidebarKey)?.label || 'Admin Panel'}

            </h1>

          </div>

          

          <div className="header-right"></div>

        </header>



        {/* Admin Content */}

        <div className="dashboard-content">

          {/* Overview Tab */}

          {activeSidebarKey === 'overview' && (

            <>

              {/* Admin Stats */}

              <Row gutter={[24, 24]} className="mb-4">

                <Col xs={24} sm={12} lg={6}>

                  <div className="metric-card glass-hover">

                    <div className="metric-icon primary">

                      <UserOutlined />

                    </div>

                    <div className="metric-content">

                      <Text className="metric-label">Total Users</Text>

                      <div className="metric-value">{users.length}</div>

                      <div className="metric-change positive">

                        <SyncOutlined /> Live data

                      </div>

                    </div>

                  </div>

                </Col>

                <Col xs={24} sm={12} lg={6}>

                  <div className="metric-card glass-hover">

                    <div className="metric-icon secondary">

                      <DesktopOutlined />

                    </div>

                    <div className="metric-content">

                      <Text className="metric-label">Resources</Text>

                      <div className="metric-value">{resources.length}</div>

                      <div className="metric-change neutral">

                        <CheckCircleOutlined /> All configured

                      </div>

                    </div>

                  </div>

                </Col>

                <Col xs={24} sm={12} lg={6}>

                  <div className="metric-card glass-hover">

                    <div className="metric-icon accent">

                      <FileTextOutlined />

                    </div>

                    <div className="metric-content">

                      <Text className="metric-label">Total Orders</Text>

                      <div className="metric-value">{orders.length}</div>

                      <div className="metric-change positive">

                        <CheckCircleOutlined /> Active

                      </div>

                    </div>

                  </div>

                </Col>

                <Col xs={24} sm={12} lg={6}>

                  <div className="metric-card glass-hover">

                    <div className="metric-icon success">

                      <FileTextOutlined />

                    </div>

                    <div className="metric-content">

                      <Text className="metric-label">Pending Orders</Text>

                      <div className="metric-value">{orders.filter((o) => o.status === 'pending').length}</div>

                      <div className="metric-change neutral">

                        <CheckCircleOutlined /> Awaiting payment

                      </div>

                    </div>

                  </div>

                </Col>

              </Row>



              {/* Quick Actions */}

              <Row gutter={[24, 24]}>

                <Col xs={24} lg={12}>

                  <Card className="glass">

                    <Card.Meta 

                      title={<div className="card-header"><span>Quick Actions</span></div>}

                      className="chart-card-meta"

                    />

                    <div className="quick-actions">

                      <button className="action-btn" onClick={openAddModal}>

                        <div className="action-icon primary">

                          <PlusOutlined />

                        </div>

                        <span>Add Resource</span>

                      </button>

                      <button className="action-btn" onClick={() => setActiveSidebarKey('users')}>

                        <div className="action-icon secondary">

                          <UserOutlined />

                        </div>

                        <span>Manage Users</span>

                      </button>

                      <button className="action-btn" onClick={() => setActiveSidebarKey('settings')}>

                        <div className="action-icon accent">

                          <SettingOutlined />

                        </div>

                        <span>Settings</span>

                      </button>

                      <a className="action-btn" href="/" target="_blank" rel="noreferrer">

                        <div className="action-icon success">

                          <GlobalOutlined />

                        </div>

                        <span>View Site</span>

                      </a>

                    </div>

                  </Card>

                </Col>

              </Row>

            </>

          )}



          {/* Resources Tab */}

          {activeSidebarKey === 'resources' && (

            <ResourceManagement

              embedded

              resourcesData={resources}

              loadingData={loading}

              onRefresh={fetchData}

              onUpdated={fetchData}

            />

          )}



          {/* Users Tab */}

          {activeSidebarKey === 'users' && (

            <Card className="glass">

              <div className="table-header">

                <Text className="section-title">All Users</Text>

                <Button type="primary" icon={<SyncOutlined />} className="admin-primary-btn" onClick={fetchData}>

                  Refresh

                </Button>

              </div>

              

              <div className="admin-users-table">

                <Table

                  columns={userColumns}

                  dataSource={users}

                  rowKey="id"

                  loading={loading}

                  className="custom-table"

                  pagination={{

                    pageSize: usersPageSize,

                    current: usersPage,

                    onChange: (page) => setUsersPage(page),

                    showSizeChanger: false,

                  }}

                  onRow={(record) => ({

                    onClick: () => navigate(`/admin/user/${record.id}`),

                    style: { cursor: 'pointer' },

                  })}

                />

              </div>



              <div className="admin-users-cards">

                {pagedUsers.map((user) => (

                  <div

                    key={user.id}

                    className="admin-user-card glass"

                    role="button"

                    tabIndex={0}

                    onClick={() => navigate(`/admin/user/${user.id}`)}

                    onKeyDown={(e) => {

                      if (e.key === 'Enter') navigate(`/admin/user/${user.id}`);

                    }}

                  >

                    <div className="admin-user-card-head">

                      <div>

                        <Text strong className="admin-user-card-title">

                          {user.firstName || ''} {user.lastName || ''}

                        </Text>

                        <div className="admin-user-card-meta">{user.email}</div>

                      </div>

                      <Tag color={user.role === 'admin' ? 'red' : 'blue'}>{user.role}</Tag>

                    </div>

                    <div className="admin-user-card-body">

                      <div className="admin-user-card-line">

                        <span className="admin-user-card-label">Joined</span>

                        <span className="admin-user-card-value">

                          {formatDateInTimezone(user.createdAt, adminPanelTimezone)}

                        </span>

                      </div>

                    </div>

                    <div className="admin-user-card-actions">

                      <Button

                        icon={<EditOutlined />}

                        size="small"

                        className="admin-action-btn"

                        onClick={(e) => {

                          e.stopPropagation();

                          handleEditUser(user);

                        }}

                      />

                      <Popconfirm

                        title="Delete this user?"

                        onConfirm={() => handleDeleteUser(user.id)}

                        okText="Yes"

                        cancelText="No"

                      >

                        <Button

                          icon={<DeleteOutlined />}

                          danger

                          size="small"

                          className="admin-action-btn"

                          onClick={(e) => e.stopPropagation()}

                        />

                      </Popconfirm>

                    </div>

                  </div>

                ))}

              </div>

              {users.length > usersPageSize && (

                <div className="resource-pagination">

                  <button

                    className="orders-page-btn"

                    onClick={() => setUsersPage((p) => Math.max(1, p - 1))}

                    disabled={usersPage === 1}

                    aria-label="Previous page"

                  >

                    {'<'}

                  </button>

                  <span className="orders-page-info">

                    {usersPage} / {Math.max(1, Math.ceil(users.length / usersPageSize))}

                  </span>

                  <button

                    className="orders-page-btn"

                    onClick={() => setUsersPage((p) => Math.min(Math.ceil(users.length / usersPageSize), p + 1))}

                    disabled={usersPage >= Math.ceil(users.length / usersPageSize)}

                    aria-label="Next page"

                  >

                    {'>'}

                  </button>

                </div>

              )}

            </Card>

          )}



          {/* Orders Tab */}

          {activeSidebarKey === 'orders' && (

            <>

              <Card className="glass" style={{ marginBottom: '24px' }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>

                  <div className="admin-orders-header">

                    <Text strong className="admin-orders-title">Order Management</Text>

                  </div>

                  <Button type="primary" icon={<PlusOutlined />} className="admin-primary-btn" onClick={() => setShowCreateOrderModal(true)}>

                    Create Order

                  </Button>

                </div>

              </Card>

              

              <Card className="glass">

                <div className="table-header">

                  <Text className="section-title">All Orders</Text>

                  <Space>

                    <Button type="primary" icon={<SyncOutlined />} className="admin-primary-btn" onClick={fetchData}>

                      Refresh

                    </Button>

                  </Space>

                </div>

                

                  <div className="admin-orders-table">

                    <Table

                      columns={orderColumns}

                      dataSource={orders}

                      rowKey="id"

                      loading={loading}

                      className="custom-table"

                      
                      pagination={{

                        pageSize: ordersPageSize,

                        current: ordersPage,

                        onChange: (page) => setOrdersPage(page),

                        showSizeChanger: false,

                      }}

                    />

                  </div>



                  <div className="admin-orders-cards">

                    {pagedOrders.map((order) => (

                      <div

                        key={order.id || order.order_number}

                        className="admin-order-card glass"


                      >

                        <div className="admin-order-card-head">

                          <div>

                            <Text strong className="admin-order-card-title">{order.order_number}</Text>

                            <div className="admin-order-card-meta">

                              {order?.User?.email || '-'}

                          </div>

                        </div>

                        <div className="admin-order-card-tags">
                          <Tag color={order.status === 'active' ? 'green' : order.status === 'completed' ? 'blue' : 'orange'}>
                            {order.status}
                          </Tag>
                          <Tag color={(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid' ? 'green' : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed' ? 'red' : 'orange'}>
                            {(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid'
                              ? 'Paid'
                              : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed'
                              ? 'Payment failed'
                              : 'Payment awaited'}
                          </Tag>
                        </div>

                      </div>

                      <div className="admin-order-card-body">

                        <div className="admin-order-card-line">
                          <span className="admin-order-card-label">Created</span>
                          <span className="admin-order-card-value">
                            {formatDateInTimezone(order.createdAt, adminPanelTimezone)}
                          </span>
                        </div>
                        <div className="admin-order-card-line">
                          <span className="admin-order-card-label">Resource</span>
                          <span className="admin-order-card-value">{order?.Resource?.name || '-'}</span>
                        </div>

                        <div className="admin-order-card-line">

                          <span className="admin-order-card-label">Start</span>

                          <span className="admin-order-card-value">

                            {formatDateInTimezone(order.start_time, adminPanelTimezone)}

                          </span>

                        </div>

                        <div className="admin-order-card-line">

                          <span className="admin-order-card-label">End</span>

                          <span className="admin-order-card-value">

                            {formatDateInTimezone(order.end_time, adminPanelTimezone)}

                          </span>

                        </div>
                        <div className="admin-order-card-line">
                          <span className="admin-order-card-label">Payment</span>
                          <span className="admin-order-card-value">
                            <Tag color={(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid' ? 'green' : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed' ? 'red' : 'orange'}>
                              {(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid'
                                ? 'Paid'
                                : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed'
                                ? 'Payment failed'
                                : 'Payment awaited'}
                            </Tag>
                          </span>
                        </div>

                        <div className="admin-order-card-line">

                          <span className="admin-order-card-label">Cost</span>

                          <span className="admin-order-card-value">
                            {order?.currency || order?.order_currency
                              ? formatAmount(Number(order.total_cost || 0), order.currency || order.order_currency)
                              : formatFromEur(Number(order.total_cost || 0))}
                          </span>

                        </div>
                        <div className="admin-order-card-line">
                          <span className="admin-order-card-label">Paynet Order ID</span>
                          <span className="admin-order-card-value">{order?.payment?.payNetOrderId || '-'}</span>
                        </div>

                      </div>

                      <div className="admin-order-card-actions">

                        <Button
                          size="small"
                          className="admin-action-btn admin-order-open-btn"
                          icon={<FileTextOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/order/${order.order_number}`);
                          }}
                        />

                        <Button icon={<EditOutlined />} size="small" className="admin-action-btn" onClick={() => handleEditOrder(order)} />

                        <Popconfirm title="Delete this order?" onConfirm={() => handleDeleteOrder(order)} okText="Yes" cancelText="No">

                          <Button icon={<DeleteOutlined />} danger size="small" className="admin-action-btn" />

                        </Popconfirm>

                      </div>

                    </div>

                  ))}

                </div>

                {orders.length > ordersPageSize && (

                  <div className="resource-pagination">

                    <button

                      className="orders-page-btn"

                      onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}

                      disabled={ordersPage === 1}

                      aria-label="Previous page"

                    >

                      {'<'}

                    </button>

                    <span className="orders-page-info">

                      {ordersPage} / {Math.max(1, Math.ceil(orders.length / ordersPageSize))}

                    </span>

                    <button

                      className="orders-page-btn"

                      onClick={() => setOrdersPage((p) => Math.min(Math.ceil(orders.length / ordersPageSize), p + 1))}

                      disabled={ordersPage >= Math.ceil(orders.length / ordersPageSize)}

                      aria-label="Next page"

                    >

                      {'>'}

                    </button>

                  </div>

                )}

              </Card>

            </>

          )}





          {/* Settings Tab */}

          {activeSidebarKey === 'settings' && (

            <Row gutter={[24, 24]}>

              <Col xs={24} lg={16}>

                <Card className="glass">

                  <Card.Meta 

                    title={<div className="card-header"><span>System Time Settings</span></div>}

                    className="chart-card-meta"

                  />

                  <Form layout="vertical">

                    <Form.Item label="System Timezone (used for platform-wide scheduling)">

                      <Select

                        className="settings-input"

                        showSearch

                        optionFilterProp="children"

                        value={systemTimezone}

                        onChange={setSystemTimezone}

                      >

                        {timezoneOptions.map((tz) => (

                          <Select.Option key={tz} value={tz}>

                            {tz}

                          </Select.Option>

                        ))}

                      </Select>

                    </Form.Item>

                    <Button type="primary" className="admin-primary-btn" onClick={saveSystemTimezone} loading={timezoneLoading}>

                      Save System Timezone

                    </Button>

                  </Form>

                </Card>

              </Col>

              <Col xs={24} lg={8}>

                <Card className="glass">

                  <Card.Meta 

                    title={<div className="card-header"><span>Admin Panel Timezone</span></div>}

                    className="chart-card-meta"

                  />

                  <Form layout="vertical">

                    <Form.Item label="Timezone for your admin dashboard">

                      <Select

                        className="settings-input"

                        showSearch

                        optionFilterProp="children"

                        value={adminPanelTimezone}

                        onChange={setAdminPanelTimezone}

                      >

                        {timezoneOptions.map((tz) => (

                          <Select.Option key={tz} value={tz}>

                            {tz}

                          </Select.Option>

                        ))}

                      </Select>

                    </Form.Item>

                    <Button type="primary" className="admin-primary-btn" onClick={saveAdminPanelTimezone} loading={timezoneLoading}>

                      Save Admin Timezone

                    </Button>

                  </Form>

                </Card>

              </Col>

            </Row>

          )}

        </div>

      </main>



      {/* Resource management now handled by dedicated ResourceManagement page */}

      {/* The resource modal has been removed in favor of the advanced management system */}



      {/* Edit User Modal */}

      <Modal

        title="Edit User"

        open={showUserModal}

        onCancel={() => { setShowUserModal(false); userForm.resetFields(); setEditingUser(null); }}

        footer={null}

        className="admin-modal admin-order-modal"

      >

        <Form form={userForm} layout="vertical" onFinish={handleUserSubmit}>

          <Form.Item name="firstName" label="First Name">

            <Input className="settings-input" />

          </Form.Item>

          <Form.Item name="lastName" label="Last Name">

            <Input className="settings-input" />

          </Form.Item>

          <Form.Item

            name="email"

            label="Email"

            rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Enter valid email' }]}

          >

            <Input className="settings-input" />

          </Form.Item>

          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Role is required' }]}>

            <Select className="settings-input">

              <Select.Option value="user">User</Select.Option>

              <Select.Option value="admin">Admin</Select.Option>

            </Select>

          </Form.Item>

          <Form.Item name="timezone" label="Timezone" rules={[{ required: true, message: 'Timezone is required' }]}>

            <Select

              className="settings-input"

              showSearch

              optionFilterProp="children"

            >

              {timezoneOptions.map((tz) => (

                <Select.Option key={tz} value={tz}>

                  {tz}

                </Select.Option>

              ))}

            </Select>

          </Form.Item>

          <Form.Item

            name="password"

            label="New Password"

            extra="Leave empty to keep current password"

            rules={[

              {

                validator: (_, value) => {

                  if (!value || value.length >= 6) return Promise.resolve();

                  return Promise.reject(new Error('Password must be at least 6 characters'));

                },

              },

            ]}

          >

            <Input.Password className="settings-input" />

          </Form.Item>

          <Form.Item>

            <Space>

              <Button type="primary" htmlType="submit">Update User</Button>

              <Button onClick={() => { setShowUserModal(false); userForm.resetFields(); setEditingUser(null); }}>

                Cancel

              </Button>

            </Space>

          </Form.Item>

        </Form>

      </Modal>



      {/* View/Edit Order Modal */}

      <Modal

        title={editingOrder ? `Order: ${editingOrder.order_number}` : 'Order Details'}

        open={showOrderModal}

        onCancel={() => { setShowOrderModal(false); setEditingOrder(null); setEditResourceComponents([]); setEditSelectedComponentIds([]); orderForm.resetFields(); }}

        footer={null}

        width={920}

        className="admin-modal admin-order-modal"

      >

        {editingOrder && (

          <>

            <Card size="small" className="admin-order-modal-meta-card">

              <Row gutter={16}>

                <Col span={12}>

                  <Text type="secondary">Order Number:</Text>

                  <div><Text strong>{editingOrder.order_number}</Text></div>

                </Col>

                {editingOrder.createdAt && !Number.isNaN(new Date(editingOrder.createdAt).getTime()) && (

                  <Col span={12}>

                    <Text type="secondary">Created:</Text>

                    <div><Text>{new Date(editingOrder.createdAt).toLocaleString()}</Text></div>

                  </Col>

                )}

              </Row>

            </Card>

            

            <Form form={orderForm} layout="vertical" onFinish={handleUpdateOrder}>

              <Row gutter={16}>

                <Col span={12}>

                  <Form.Item name="user_id" label="User">

                    <Select className="settings-input" showSearch optionFilterProp="children">

                      {users.map((user) => (

                        <Select.Option key={user.id} value={user.id}>

                          {user.email}

                        </Select.Option>

                      ))}

                    </Select>

                  </Form.Item>

                </Col>

                <Col span={12}>

                  <Form.Item name="resource_id" label="Resource">

                    <Select

                      className="settings-input"

                      showSearch

                      optionFilterProp="children"

                      onChange={handleEditResourceChange}

                    >

                      {resources.map((resource) => (

                        <Select.Option key={resource.id} value={resource.id}>

                          {resource.name}

                        </Select.Option>

                      ))}

                    </Select>

                  </Form.Item>

                </Col>

              </Row>

              

              <Form.Item name="status" label="Status">

                <Select className="settings-input">

                  <Select.Option value="pending">Pending</Select.Option>

                  <Select.Option value="active">Active</Select.Option>

                  <Select.Option value="completed">Completed</Select.Option>

                  <Select.Option value="cancelled">Cancelled</Select.Option>

                </Select>

              </Form.Item>

              <Form.Item name="payment_status" label="Payment Status">
                <Select className="settings-input">
                  <Select.Option value="payment_awaited">Payment awaited</Select.Option>
                  <Select.Option value="paid">Paid</Select.Option>
                  <Select.Option value="payment_failed">Payment failed</Select.Option>
                </Select>
              </Form.Item>

              

              <Row gutter={16}>

                <Col span={12}>

                  <Form.Item name="created_at" label="Created At">

                    <DatePicker

                      showTime

                      format="YYYY-MM-DD HH:mm"

                      className="settings-input"

                      style={{ width: '100%' }}

                    />

                  </Form.Item>

                </Col>

                <Col span={12}>

                  <Form.Item name="start_time" label="Start Time">

                    <DatePicker

                      showTime

                      format="YYYY-MM-DD HH:mm"

                      className="settings-input"

                      style={{ width: '100%' }}

                    />

                  </Form.Item>

                </Col>

                <Col span={12}>

                  <Form.Item name="end_time" label="End Time">

                    <DatePicker

                      showTime

                      format="YYYY-MM-DD HH:mm"

                      className="settings-input"

                      style={{ width: '100%' }}

                    />

                  </Form.Item>

                </Col>

              </Row>



              <Row gutter={16}>

                <Col span={12}>

                  <Form.Item name="payment_id" label="Payment ID">

                    <Input className="settings-input" placeholder="Payment reference" />

                  </Form.Item>

                </Col>

                <Col span={12}>

                  <Form.Item label="Pricing Formula">

                    <Input

                      disabled

                      className="settings-input"

                      value={`€${Number.isFinite(estimatedHourlyRate) ? estimatedHourlyRate.toFixed(2) : '0.00'}/h x ${editHours.toFixed(2)}h`}

                    />

                  </Form.Item>

                </Col>

              </Row>



              <Divider className="admin-order-modal-divider">Order Items</Divider>

              {editResourceComponents.length > 0 ? (

                <>

                  {Object.entries(groupedEditComponents).map(([type, components]) => (

                    <div key={type} className="admin-order-modal-group">

                      <Text strong style={{ display: 'block', marginBottom: 8 }}>

                        {type.charAt(0).toUpperCase() + type.slice(1)}

                      </Text>

                      <Select

                        placeholder={`Select ${type}`}

                        style={{ width: '100%' }}

                        value={editSelectedComponentIds.find((id) =>

                          components.some((comp) => Number(comp.id) === Number(id))

                        )}

                        onChange={(id) => handleEditComponentSelect(type, id)}

                        optionFilterProp="children"

                      >

                        {components.map((comp) => (

                          <Select.Option key={comp.id} value={comp.id}>

                            {comp.value}

                            {comp.is_free ? ' (Free)' : ` (+€${comp.price_delta}/hr)`}

                          </Select.Option>

                        ))}

                      </Select>

                    </div>

                  ))}



                </>

              ) : (

                <Text type="secondary">No configurable components for selected resource.</Text>

              )}

              

              <Form.Item name="total_cost" label="Total Cost">

                <Input className="settings-input" disabled />

              </Form.Item>

              

              <Form.Item>

                <Space>

                  <Button type="primary" htmlType="submit">Save Changes</Button>

                  <Button onClick={() => { setShowOrderModal(false); setEditingOrder(null); setEditResourceComponents([]); setEditSelectedComponentIds([]); orderForm.resetFields(); }}>

                    Cancel

                  </Button>

                </Space>

              </Form.Item>

            </Form>

          </>

        )}

      </Modal>



      {/* Create Order Modal */}

      <Modal

        title="Create New Order"

        open={showCreateOrderModal}

        onCancel={() => { setShowCreateOrderModal(false); createOrderForm.resetFields(); setSelectedComponents([]); setResourceComponents([]); }}

        footer={null}

        width={700}

        className="admin-modal admin-create-order-modal"

      >

        <Form form={createOrderForm} layout="vertical" onFinish={handleCreateOrderWithComponents}>

          <Row gutter={16}>

            <Col span={12}>

              <Form.Item name="user_id" label="User" rules={[{ required: true, message: 'Please select a user' }]}>

                <Select className="settings-input" showSearch placeholder="Select a user" optionFilterProp="children">

                  {users.filter(u => u.role === 'user').map(user => (

                    <Select.Option key={user.id} value={user.id}>{user.email}</Select.Option>

                  ))}

                </Select>

              </Form.Item>

            </Col>

            <Col span={12}>

              <Form.Item name="resource_id" label="Resource" rules={[{ required: true, message: 'Please select a resource' }]}>

                <Select 

                  className="settings-input" 

                  showSearch 

                  placeholder="Select a resource" 

                  optionFilterProp="children"

                  onChange={handleResourceChange}

                >

                  {resources.filter(r => r.available).map(resource => (

                    <Select.Option key={resource.id} value={resource.id}>

                      {resource.name} - €{resource.base_price_per_hour || resource.price_per_hour}/hr ({resource.gpu_model || resource.type})

                    </Select.Option>

                  ))}

                </Select>

              </Form.Item>

            </Col>

          </Row>

          

          <Row gutter={16}>

            <Col span={12}>

              <Form.Item name="created_at" label="Created At">

                <DatePicker

                  showTime

                  format="YYYY-MM-DD HH:mm"

                  className="settings-input"

                  style={{ width: '100%' }}

                />

              </Form.Item>

            </Col>

            <Col span={12}>

              <Form.Item name="start_time" label="Start Time">

                <DatePicker

                  showTime

                  format="YYYY-MM-DD HH:mm"

                  className="settings-input"

                  style={{ width: '100%' }}

                />

              </Form.Item>

            </Col>

          </Row>



          <Row gutter={16}>

            <Col span={12}>

              <Form.Item name="end_time" label="End Time">

                <DatePicker

                  showTime

                  format="YYYY-MM-DD HH:mm"

                  className="settings-input"

                  style={{ width: '100%' }}

                />

              </Form.Item>

            </Col>

          </Row>



          <Row gutter={16}>

            <Col span={12}>

              <Form.Item name="hours" label="Duration (hours)" rules={[{ required: true, message: 'Please enter hours' }]}>

                <Input type="number" min={0} max={720} className="settings-input" placeholder="e.g., 2" />

              </Form.Item>

            </Col>

            <Col span={12}>

              <Form.Item name="minutes" label="Minutes" rules={[{ required: true, message: 'Please enter minutes' }]}>

                <Input type="number" min={0} max={59} className="settings-input" placeholder="e.g., 30" />

              </Form.Item>

            </Col>

          </Row>

          <Text type="secondary" className="admin-order-modal-note">

            You can select start and end time — duration will be calculated automatically.

          </Text>

          

          {/* Components Selection */}

          {resourceComponents.length > 0 && (

            <Card size="small" title="Select Components" className="admin-create-order-components-card">

              {Object.entries(groupedComponents).map(([type, components]) => (

                <div key={type} className="admin-create-order-group">

                  <Text strong style={{ display: 'block', marginBottom: 8 }}>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>

                  <Select

                    placeholder={`Select ${type}`}

                    style={{ width: '100%' }}

                    value={selectedComponents.find(c => 

                      resourceComponents.find(rc => rc.id === c.id)?.type === type

                    )?.id}

                    onChange={(id) => handleComponentSelect(type, id)}

                    optionFilterProp="children"

                  >

                    {components.map(comp => (

                      <Select.Option key={comp.id} value={comp.id}>

                        {comp.value} 

                        {comp.is_free ? (

                          <Text type="success"> (Free)</Text>

                        ) : (

                          <Text type="warning"> (+€{comp.price_delta}/hr)</Text>

                        )}

                      </Select.Option>

                    ))}

                  </Select>

                </div>

              ))}

              

              <div className="admin-create-order-summary">

                <Row gutter={16}>

                  <Col span={8}>

                    <Text type="secondary">Base Price:</Text>

                    <div><Text strong>€{currentResource?.base_price_per_hour || currentResource?.price_per_hour || 0}/hr</Text></div>

                  </Col>

                  <Col span={8}>

                    <Text type="secondary">Components:</Text>

                    <div><Text strong>€{componentsCost}/hr</Text></div>

                  </Col>

                  <Col span={8}>

                    <Text type="secondary">Total:</Text>

                    <div><Text strong className="admin-create-order-total">€{totalOrderCost}/hr</Text></div>

                  </Col>

                </Row>

              </div>

            </Card>

          )}

          

          <Form.Item name="payment_id" label="Payment ID">

            <Input className="settings-input" placeholder="External payment system ID" />

          </Form.Item>

          

          <Form.Item>

            <Space>

              <Button type="primary" htmlType="submit">Create Order</Button>

              <Button onClick={() => { setShowCreateOrderModal(false); createOrderForm.resetFields(); setSelectedComponents([]); setResourceComponents([]); }}>

                Cancel

              </Button>

            </Space>

          </Form.Item>

        </Form>

      </Modal>

    </div>

  );

}



export default Admin;






